import { useEffect, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion, useAnimation } from 'framer-motion'
import { CommentsPanel } from '../products/CommentsPanel'
import type { DiscussionThread } from '../../lib/discussions'

interface FeedCommentsOverlayProps {
  contributionId: string
  currentUserId: string
  currentUser?: {
    first_name: string
    last_name: string
    username: string
    avatar_url?: string
  }
  ownerComment?: string
  ownerUser?: {
    first_name: string
    last_name: string
    username: string
    avatar_url?: string
  }
  panelSide: 'left' | 'right'
  cardRect?: { top: number; bottom: number; left: number; right: number }
  onClose: () => void
  onCommentAdded?: () => void
  onCommentDeleted?: () => void
  syntheticThread?: DiscussionThread
}

const PANEL_WIDTH = 500
const HEADER_HEIGHT = 80

export function FeedCommentsOverlay({
  contributionId,
  currentUserId,
  currentUser,
  ownerComment,
  ownerUser,
  panelSide,
  cardRect,
  onClose,
  onCommentAdded,
  onCommentDeleted,
  syntheticThread
}: FeedCommentsOverlayProps) {
  const navigate = useNavigate()
  const [panelReady, setPanelReady] = useState(false)
  const [isExpanding, setIsExpanding] = useState(false)
  const panelControls = useAnimation()
  const backdropControls = useAnimation()
  const connectorControls = useAnimation()

  // Scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  // Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isExpanding) onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, isExpanding])

  // Delay connector line until panel has slid in
  useEffect(() => {
    const timer = setTimeout(() => setPanelReady(true), 350)
    return () => clearTimeout(timer)
  }, [])

  const isLeft = panelSide === 'left'

  // Compute how far to move the panel to reach center
  const expandXOffset = useMemo(() => {
    if (isLeft) {
      // Panel starts at left:0, needs to move right to center
      return (window.innerWidth - PANEL_WIDTH) / 2
    } else {
      // Panel starts at right:0, needs to move left to center
      return -((window.innerWidth - PANEL_WIDTH) / 2)
    }
  }, [isLeft])

  // Run expand animation sequence
  const handleExpand = async () => {
    setIsExpanding(true)

    // Phase 1: Connector dissolves, panel lifts and glides to center, backdrop brightens
    await Promise.all([
      connectorControls.start({
        opacity: 0,
        transition: { duration: 0.15 }
      }),
      panelControls.start({
        x: expandXOffset,
        y: -8,
        scale: 1.02,
        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15)',
        transition: {
          type: 'spring',
          damping: 24,
          stiffness: 180,
          mass: 0.9,
        }
      }),
      backdropControls.start({
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        transition: { duration: 0.4, ease: 'easeInOut' }
      }),
    ])

    // Phase 2: Brief hold, then flash white and navigate
    await Promise.all([
      panelControls.start({
        opacity: 0,
        scale: 1.06,
        y: -16,
        transition: { duration: 0.2, ease: 'easeIn' }
      }),
      backdropControls.start({
        backgroundColor: 'rgba(255, 255, 255, 1)',
        transition: { duration: 0.2, ease: 'easeIn' }
      }),
    ])

    onClose()
    navigate(`/discussions/contribution/${contributionId}`, { state: { arrivedFromFlyover: true, syntheticThread } })
  }

  // Compute connector line coordinates
  const connectorLine = (() => {
    if (!cardRect || !panelReady || isExpanding) return null

    const cardMidY = (cardRect.top + cardRect.bottom) / 2
    // Clamp Y to be within the visible panel area
    const y = Math.max(HEADER_HEIGHT + 20, Math.min(cardMidY, window.innerHeight - 20))

    if (isLeft) {
      // Panel on left: line from panel right edge to card left edge
      return { x1: PANEL_WIDTH, y1: y, x2: cardRect.left, y2: y }
    } else {
      // Panel on right: line from card right edge to panel left edge
      return { x1: cardRect.right, y1: y, x2: window.innerWidth - PANEL_WIDTH, y2: y }
    }
  })()

  return createPortal(
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        animate={isExpanding ? backdropControls : { opacity: 1, backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[30]"
        onClick={!isExpanding ? onClose : undefined}
      />

      {/* Connector line */}
      {connectorLine && (
        <motion.svg
          initial={{ opacity: 0 }}
          animate={isExpanding ? connectorControls : { opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[35] pointer-events-none"
          width="100%"
          height="100%"
        >
          <line
            x1={connectorLine.x1}
            y1={connectorLine.y1}
            x2={connectorLine.x2}
            y2={connectorLine.y2}
            stroke="#57241F"
            strokeWidth={2}
            strokeDasharray="6 4"
            opacity={0.6}
          />
          {/* Small dot at card end */}
          <circle
            cx={isLeft ? connectorLine.x2 : connectorLine.x1}
            cy={connectorLine.y1}
            r={4}
            fill="#57241F"
            opacity={0.6}
          />
          {/* Small dot at panel end */}
          <circle
            cx={isLeft ? connectorLine.x1 : connectorLine.x2}
            cy={connectorLine.y1}
            r={4}
            fill="#57241F"
            opacity={0.6}
          />
        </motion.svg>
      )}

      {/* Comments Panel */}
      <motion.div
        initial={{ x: isLeft ? '-100%' : '100%', opacity: 1, scale: 1, y: 0 }}
        animate={isExpanding ? panelControls : { x: 0, opacity: 1, scale: 1, y: 0 }}
        exit={isExpanding ? undefined : { x: isLeft ? '-100%' : '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 220 }}
        className={`fixed top-[80px] bottom-0 w-[500px] z-[40] shadow-2xl rounded-t-xl overflow-hidden ${
          isLeft ? 'left-0' : 'right-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <CommentsPanel
          key={contributionId}
          contributionId={contributionId}
          currentUserId={currentUserId}
          currentUser={currentUser ? { ...currentUser, avatar_url: currentUser.avatar_url || '' } : undefined}
          ownerComment={ownerComment}
          ownerUser={ownerUser ? { ...ownerUser, avatar_url: ownerUser.avatar_url || '' } : undefined}
          onClose={onClose}
          isMobile={false}
          onCommentAdded={onCommentAdded}
          onCommentDeleted={onCommentDeleted}
          onExpand={handleExpand}
        />
      </motion.div>
    </>,
    document.body
  )
}
